<template>
  <div class="content-main" v-if="importStatus">
    <div class="content-main__centered">
      <div v-if="importFinished">
        <h4 class="appMain--text mb-1">Success!</h4>
        <p class="mb-6">Your contacts have been uploaded.</p>
      </div>
      <div v-else>
        <p class="mb-1">
          Your import into
          <strong>{{ importStatus.bucket_title }}</strong> has a status of
          <v-chip label color="primary" small class="pa-2 pt-0 pb-0 ml-1 mr-1">
            {{ status }}
          </v-chip>
        </p>
        <p class="mb-6">
          Once we have finished importing your file, we will start merging into
          contacts.
        </p>
      </div>

      <v-data-table
        class="status-table styled-table mb-6"
        :headers="headers"
        :items="tableItems"
        :loading="loading"
        hide-default-footer
        :itemsPerPage="-1"
        :mobile-breakpoint="0"
      />

      <v-btn
        v-if="hasResultStats"
        color="primary"
        class="mb-6"
        small
        @click="viewReport"
      >
        View Full Report
      </v-btn>

      <div class="mt-4" v-if="!hideTagGroup">
        <p class="mb-2">Create a group for fresh contacts with a tag:</p>
        <div v-for="tag in importedContactsTags" :key="tag + 'i'" class="mb-2">
          <v-btn
            color="appMain"
            class="tag-button"
            small
            @click="createGroupFor(tag)"
          >
            {{ tag }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { format } from 'date-fns';
import EventBus from '@/factories/eventBus';

export default {
  name: 'ContactsImportStatus',
  props: {
    importId: {
      type: String,
      default: '',
    },
    hideTagGroup: Boolean,
  },
  data() {
    return {
      headers: [
        { text: 'Status', value: 'status', sortable: false, width: 160 },
        { text: 'Count', value: 'count', sortable: false },
      ],
      statusOrder: [
        'awaiting',
        'pending',
        'created',
        'merged',
        'skipped',
        'failed',
      ],
      loading: false,
      importStatus: {},
    };
  },
  computed: {
    ...mapGetters('fields', ['getSingleField', 'loaded']),
    tableItems() {
      if (!this.importStatus) return [];

      const stats = this.importStatus.stats || {};
      const resultStats = this.importStatus.result_stats || {};

      const items = [];

      const awaiting = (stats.awaiting || 0) + (stats.pending || 0);
      if (awaiting > 0) {
        items.push({ status: 'pending', count: awaiting });
      }

      ['created', 'merged', 'skipped', 'failed'].forEach((key) => {
        const count = resultStats[key] || 0;
        if (count > 0) {
          items.push({ status: key, count });
        }
      });

      return items.sort(
        (a, b) =>
          this.statusOrder.indexOf(a.status) -
          this.statusOrder.indexOf(b.status)
      );
    },
    importFinished() {
      const stats = this.importStatus?.stats;
      if (!stats) return false;

      if (Object.values(stats).every((stat) => stat === 0)) {
        return false;
      }
      return !stats.pending && !stats.awaiting;
    },
    status() {
      if (this.importFinished) return 'Finished';
      return this.importStatus.status;
    },
    hasResultStats() {
      const rs = this.importStatus?.result_stats;
      return rs && Object.keys(rs).length > 0;
    },
    reportFilters() {
      return { import_id: this.importId, data: 1 };
    },
    importedContactsTags() {
      const currentTags = this.importStatus?.tags || [];
      const defaultTag = 'import_' + format(new Date(), 'MM_dd_yy');
      return [...currentTags, defaultTag];
    },
  },
  methods: {
    ...mapActions(['getContactsImportStatus']),
    ...mapActions('fields', ['load']),
    async checkStatus() {
      await this.$nextTick();

      this.loading = true;

      try {
        this.importStatus = await this.getContactsImportStatus(this.importId);
        this.setReloadTimeout();
      } finally {
        this.loading = false;
      }
    },

    setReloadTimeout() {
      if (this.importFinished) return;

      setTimeout(() => {
        this.checkStatus();
      }, 2000);
    },

    viewReport() {
      EventBus.$emit('openReportModal', {
        reportId: 'contactsImportResults',
        filters: this.reportFilters,
        title: 'Import Results',
      });
    },

    createGroupFor(tag) {
      const { bucketId, name } = this.getSingleField('tags') || {};
      const field = `${bucketId}.${name}`;

      this.$router.push({
        name: 'group-create',
        params: {
          outerFilter: {
            $and: [{ [field]: { $in: tag } }],
          },
        },
      });
    },

    checkFields() {
      if (this.loaded) return;
      this.load();
    },
  },
  mounted() {
    this.checkFields();
    setTimeout(() => {
      this.checkStatus();
    }, 300);
  },
};
</script>

<style lang="less">
.status-table {
  tbody {
    tr:nth-child(odd):hover,
    tr:nth-child(odd) {
      background-color: var(--v-lightGray-base) !important;
    }
  }
  tr {
    cursor: default;
    &:hover {
      background-color: #fff !important;
    }
  }
  td {
    text-transform: capitalize;
    font-size: 14px !important;
  }
}

.v-btn.tag-button {
  text-transform: none;
}
</style>
